import React, {
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState,
} from 'react';
import isEqual from 'lodash.isequal';
import { storage } from '@treatwell/ui';
import { BrowsePageOutput } from 'js/model/rainbow/browse-page/BrowsePageOutput';
import {
  composeFetchBrowse,
  getBrowseApiBaseUrl,
  isBrowseApiV2Enabled,
} from 'js/service/browseService';
import { VenueListingCriteria } from 'js/model/rainbow/browse-page/VenueListingCriteria';
import {
  parseUri,
  generateUri,
  windowLocationQueryParameters,
} from 'js/helpers/uri-util';
import { Location } from 'js/model/rainbow/browse-page/VenueListingSpecificationOutput';
import { getCurrentPosition } from 'js/helpers/geo-location';
import { setHost } from 'js/helpers/browse-uris';
import { historyPush } from 'js/helpers/history';
import * as navigationLocation from 'js/components/Navigation/navigation-location';
import { Context } from 'js/components/LocaleWrapper';
import { useGrowthBook } from '@growthbook/growthbook-react';
import { getBrowseExperimentVariant } from 'js/growthbook/browse-experiment-config';
import { ViewMode, viewModeForValue } from './ViewModeToggle';
import { getPageSize } from './page-size';
import { trackSearch } from './tracking/trackSearch';
import { createInitialState, reducer } from './reducer';
import { enrichBrowsePageOutputWithLocationIfMissing } from './utils';

type CurrentTreatmentTypeParameters = {
  treatmentCategoryIds?: number[];
  treatmentCategoryGroupId?: number;
  venueTypeId?: number;
  menuItemTypes?: string[];
  nights?: number;
};

export enum browseRemovalParameterType {
  DATE = 'availableOn',
  STARTHOURS = 'timeRange.from',
  ENDHOURS = 'timeRange.to',
  NIGHTS = 'fixedStayNights',
  MAXDURATION = 'maxServiceDuration',
  ACCOLADE = 'accolade',
  BOOKINGTYPE = 'bookingType',
  PRICERANGEFROM = 'priceRange.from',
  PRICERANGETO = 'priceRange.to',
  VENUETYPE = 'venueType',
  TREATMENT = 'treatments',
  TREATMENTTYPE = 'treatmentType',
  AMENITIES = 'amenities',
  BRANDS = 'brands',
  MIN_REVIEW_RATING = 'minReviewRating',
}

export enum AddLocation {
  Geo,
  CurrentPage,
  CurrentPageWithRadius,
}

export type ChangeBrowseDataFunction = (
  parameters: VenueListingCriteria,
  removeParameters?: browseRemovalParameterType[],
  addLocation?: AddLocation,
  triggeredByHistoryNavigation?: boolean,
  viewMode?: ViewMode,
  aliasId?: number
) => Promise<void>;

const getViewMode = () => {
  const queryParameters = windowLocationQueryParameters();

  return viewModeForValue(queryParameters.view);
};

export const changeBrowseDataContext =
  React.createContext<ChangeBrowseDataFunction>(() => Promise.resolve());

export const BrowseData = ({
  initialBrowsePageOutput,
  renderPage,
}: {
  initialBrowsePageOutput: BrowsePageOutput;
  renderPage: (
    browsePageOutput: BrowsePageOutput,
    loading: boolean
  ) => React.ReactNode;
}) => {
  const { pageData, channel } = useContext(Context);
  const gb = useGrowthBook();
  const isV2Enabled = isBrowseApiV2Enabled(gb);
  const [state, dispatch] = useReducer(
    reducer,
    {
      browsePageOutput: initialBrowsePageOutput,
      channelLocation: channel.country,
    },
    createInitialState
  );

  const getInitialTreatmentTypeParameters =
    (): CurrentTreatmentTypeParameters => {
      const { specification } = initialBrowsePageOutput;

      let initialTreatmentTypeParameters = {};

      if (specification.treatmentCategories) {
        initialTreatmentTypeParameters = {
          ...initialTreatmentTypeParameters,
          treatmentCategoryIds: specification.treatmentCategories.map(
            (category) => category.id
          ),
        };
      }

      if (specification.treatmentCategoryGroup) {
        initialTreatmentTypeParameters = {
          ...initialTreatmentTypeParameters,
          treatmentCategoryGroupId: specification.treatmentCategoryGroup.id,
        };
      }

      if (specification.venueType) {
        initialTreatmentTypeParameters = {
          ...initialTreatmentTypeParameters,
          venueTypeId: specification.venueType.id,
        };
      }

      if (specification.menuItemTypes) {
        initialTreatmentTypeParameters = {
          ...initialTreatmentTypeParameters,
          menuItemTypes: specification.menuItemTypes,
          nights: specification.nights,
        };
      }

      return initialTreatmentTypeParameters;
    };

  const [currentTreatmentTypeParameters, setCurrentTreatmentTypeParameters] =
    useState(getInitialTreatmentTypeParameters);

  const currentPageLocationAsParameter = (currentLocation: Location) => {
    if (currentLocation.tree) {
      return {
        tree: {
          id: currentLocation.tree.id,
        },
      };
    }

    if (currentLocation.point) {
      return {
        point: {
          latitude: currentLocation.point.lat,
          longitude: currentLocation.point.lon,
        },
      };
    }

    if (currentLocation.external) {
      return {
        external: {
          id: currentLocation.external.id,
          description: currentLocation.external.name,
        },
      };
    }

    if (currentLocation.postalReference) {
      return {
        postalReferenceId: currentLocation.postalReference.id,
      };
    }

    throw new Error('Unhandled location type');
  };

  const changeBrowseData: ChangeBrowseDataFunction = useCallback(
    async (
      parameters,
      removeParameters,
      addLocation?: AddLocation,
      triggeredByHistoryNavigation = false,
      viewMode?: ViewMode,
      aliasId?: number
    ) => {
      // TODO: rename
      const removeParametersFn = (
        currentPath: string,
        removeParameters?: browseRemovalParameterType[]
      ) => {
        if (!removeParameters || removeParameters.length === 0) {
          return currentPath;
        }

        // NOTE - This is special case when we want to clear some parameters from new browse data request

        // 1) construct uri path from current pathname
        const { code, languageCode } = pageData.channel;
        const parsedUri = parseUri(currentPath, code, languageCode);

        if (!parsedUri) {
          return currentPath;
        }

        // 2) remove parameters
        for (const parameter of removeParameters) {
          delete parsedUri.values[parameter];
        }

        // 3) generate new uri path
        return (
          generateUri(
            'browse',
            parsedUri.values,
            pageData.channel.code,
            pageData.channel.languageCode
          ) || currentPath
        );
      };

      const persistNavigationLocation = (
        browsePageOutput: BrowsePageOutput
      ) => {
        const { location } = browsePageOutput.specification || {
          location: undefined,
        };

        if (!location) {
          return;
        }

        const languageCode = pageData.channel.languageCode;

        if (location.tree) {
          navigationLocation.removeAllExcept(languageCode);
          navigationLocation.save(
            { location: location.tree.normalisedName },
            languageCode
          );
        }

        if (location.postalReference) {
          navigationLocation.removeAllExcept(languageCode);
          navigationLocation.save(
            {
              postalReference: location.postalReference.normalisedName,
            },
            languageCode
          );
        }

        if (location.external) {
          navigationLocation.removeAllExcept(languageCode);
          navigationLocation.save(
            {
              externalLocation: location.external.id,
            },
            languageCode
          );
        }

        if (location.point) {
          navigationLocation.removeAllExcept(languageCode);
          navigationLocation.save(
            {
              searchAreaGeocode: `${location.point.lat},${location.point.lon}`,
            },
            languageCode
          );
        }
      };

      const transformParametersWithOptions = async (
        parameters: VenueListingCriteria,
        addLocation?: AddLocation,
        triggeredByHistoryNavigation?: boolean
      ) => {
        const currentLocation = state.browsePageOutput.specification.location;
        const newParameters: typeof parameters = { ...parameters };

        // Add current page location used for radius changes as API requires location + radius
        if (addLocation === AddLocation.CurrentPage && currentLocation) {
          newParameters.location = {
            ...newParameters.location,
            ...currentPageLocationAsParameter(currentLocation),
          };
        }

        // Non-location change keeps location and radius
        if (
          addLocation === AddLocation.CurrentPageWithRadius &&
          currentLocation &&
          currentLocation.radius
        ) {
          newParameters.location = {
            ...newParameters.location,
            ...currentPageLocationAsParameter(currentLocation),
            radius: currentLocation.radius.distance,
          };
        }

        // Add current geo location
        if (addLocation === AddLocation.Geo) {
          newParameters.location = {
            ...(await currentGeoLocationAsParameter()),
          };
        }

        // All operations other than pagination and history changes should reset the page to 0
        if (!newParameters.page && !triggeredByHistoryNavigation) {
          newParameters.page = 0;
        }

        return newParameters;
      };

      const fetchNewData = async (
        parameters: VenueListingCriteria,
        removeParameters?: browseRemovalParameterType[],
        addLocation?: AddLocation,
        triggeredByHistoryNavigation?: boolean,
        viewMode?: ViewMode,
        aliasId?: number
      ) => {
        dispatch({ type: 'SEARCH_START' });

        try {
          const newViewMode = triggeredByHistoryNavigation
            ? getViewMode()
            : viewMode;

          const currentPath = removeParametersFn(
            window.location.pathname,
            removeParameters
          );

          const variant = getBrowseExperimentVariant(gb);

          if (variant !== 'none') {
            parameters.variant = variant;
          }

          const transformedParameters = await transformParametersWithOptions(
            parameters,
            addLocation,
            triggeredByHistoryNavigation
          );

          const fetchBrowse = composeFetchBrowse(
            getBrowseApiBaseUrl(isV2Enabled),
            isV2Enabled
          );

          const browsePageOutput = await fetchBrowse(
            pageData,
            currentPath,
            undefined,
            transformedParameters
          );

          persistNavigationLocation(browsePageOutput);

          const historyUrl = new URL(
            setHost(
              browsePageOutput.canonicalUri,
              window.location.protocol,
              window.location.host
            ) + window.location.search
          );

          if (newViewMode) {
            if (newViewMode !== ViewMode.Hybrid) {
              historyUrl.searchParams.append('view', newViewMode);
            } else {
              historyUrl.searchParams.delete('view');
            }
          }

          if (
            !triggeredByHistoryNavigation &&
            historyUrl.toString() !== window.location.href
          ) {
            historyPush({}, '', historyUrl);
          }

          browsePageOutput.aliasId = aliasId;
          browsePageOutput.viewMode =
            newViewMode || browsePageOutput.viewMode || getViewMode();

          dispatch({
            type: 'SEARCH_SUCCESS',
            payload: enrichBrowsePageOutputWithLocationIfMissing(
              browsePageOutput,
              channel.country
            ),
          });

          window.scroll(0, 0);
        } catch (e) {
          dispatch({ type: 'SEARCH_FAILURE' });

          throw e;
        }
      };

      const shouldClearPriceRange = (parameters: VenueListingCriteria) => {
        const treatmentTypeParameterKeys: (keyof CurrentTreatmentTypeParameters)[] =
          [
            'venueTypeId',
            'treatmentCategoryIds',
            'treatmentCategoryGroupId',
            'menuItemTypes',
          ];

        let newTreatmentTypeParameters = { ...currentTreatmentTypeParameters };

        for (const key of treatmentTypeParameterKeys) {
          if (
            parameters[key] &&
            !isEqual(parameters[key], currentTreatmentTypeParameters[key])
          ) {
            newTreatmentTypeParameters = {
              ...newTreatmentTypeParameters,
              [key]: parameters[key],
            };
          }
        }

        if (parameters.menuItemTypes) {
          newTreatmentTypeParameters = {
            ...newTreatmentTypeParameters,
            nights: parameters.nights,
          };
        }

        if (
          !isEqual(currentTreatmentTypeParameters, newTreatmentTypeParameters)
        ) {
          setCurrentTreatmentTypeParameters(newTreatmentTypeParameters);

          return true;
        }

        return false;
      };

      let newViewMode = viewMode;

      if (triggeredByHistoryNavigation) {
        const navigationViewMode = getViewMode();

        if (navigationViewMode !== state.browsePageOutput.viewMode) {
          newViewMode = navigationViewMode;
        }
      }

      if (newViewMode) {
        await fetchNewData(
          { pageSize: getPageSize(newViewMode) },
          undefined,
          undefined,
          triggeredByHistoryNavigation,
          newViewMode,
          aliasId
        );
      } else {
        let parametersToRemove = removeParameters ? [...removeParameters] : [];

        if (shouldClearPriceRange(parameters)) {
          parametersToRemove = [
            ...parametersToRemove,
            browseRemovalParameterType.PRICERANGEFROM,
            browseRemovalParameterType.PRICERANGETO,
          ];
        }

        await fetchNewData(
          {
            ...parameters,
            pageSize: getPageSize(state.browsePageOutput.viewMode),
          },
          parametersToRemove,
          addLocation,
          triggeredByHistoryNavigation,
          undefined,
          aliasId
        );
      }
    },
    [
      channel.country,
      currentTreatmentTypeParameters,
      pageData,
      state.browsePageOutput.specification.location,
      state.browsePageOutput.viewMode,
    ]
  );

  const onPopState = useCallback(() => {
    changeBrowseData({}, [], undefined, true);
  }, [changeBrowseData]);

  useEffect(() => {
    const treatmentAliasIdFromHomePage = storage.session.getItem(
      'searchAliasTreatment'
    );

    window.addEventListener('popstate', onPopState);

    trackSearch(
      'treatment_searched',
      channel.country.countryCode,
      state.browsePageOutput.specification,
      state.browsePageOutput.results,
      treatmentAliasIdFromHomePage || undefined,
      state.browsePageOutput.pagination.totalElements
    );

    // needs to be removed so that if a user refreshes the browse page in the same session or use the nav bar this will be sent incorrectly
    storage.session.removeItem('searchAliasTreatment');

    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, [
    channel.country.countryCode,
    onPopState,
    state.browsePageOutput.pagination.totalElements,
    state.browsePageOutput.results,
    state.browsePageOutput.specification,
  ]);

  /**
   * TODO:
   * Check the usefulness of this function!
   * The `treatment_searched` event is sent within an effect and thus when we
   * visit the page for the first time and when we update it by applying
   * filters, for example.
   *
   * For snowplow events, check if they are still used.
   */
  /* const trackNewBrowsePage = async (browsePageOutput: BrowsePageOutput) => {
    await yieldToMain();

    // Clear any old venueIds to avoid undesired merges
    push({
      listing: {
        venues: undefined,
      },
    });

    trackEvent('virtualPageView', {
      page: {
        path: window.location.pathname,
      },
      ...browsePageDataLayer(browsePageOutput, pageData.channel).pageSpecific,
    });

    trackSearch(
      'treatment_searched',
      channel.country.countryCode,
      browsePageOutput.specification,
      browsePageOutput.results,
      browsePageOutput.aliasId
    );
  }; */

  return (
    <changeBrowseDataContext.Provider value={changeBrowseData}>
      {renderPage(state.browsePageOutput, state.isLoading)}
    </changeBrowseDataContext.Provider>
  );
};

export async function currentGeoLocationAsParameter(): Promise<
  VenueListingCriteria['location']
> {
  const geoLocation = await getCurrentPosition(10 * 1000);

  return {
    point: {
      latitude: geoLocation.coords.latitude,
      longitude: geoLocation.coords.longitude,
    },
  };
}
